/**
 * Number formatting shared by the research-statistics widgets.
 *
 * The widgets format through `useToolNumber()`, so the reader's locale decides the
 * decimal mark; these helpers only decide how many digits, and the APA conventions:
 * statistics that cannot exceed 1 in absolute value (α, κ, r, p) drop the leading zero,
 * and a p-value below .001 is written "< .001".
 */

export type Formatter = (
  value: number,
  options?: Intl.NumberFormatOptions
) => string;

const LEADING_ZERO = /^(-|−)?0(?=[.,])/;
const HYPHEN_MINUS = /^-/;

/** A value with exactly `digits` decimals. */
export function fixed(fmt: Formatter, value: number, digits: number): string {
  return fmt(value, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).replace(HYPHEN_MINUS, "−");
}

/** APA style for a bounded statistic: ".92", "−.15". */
export function apaBounded(fmt: Formatter, value: number, digits = 2): string {
  return fixed(fmt, value, digits).replace(LEADING_ZERO, "$1");
}

/** A p-value: three decimals, or "< 0.001" (APA: "< .001" with `apa`). */
export function formatP(fmt: Formatter, p: number, apa = false): string {
  if (!Number.isFinite(p)) {
    return "—";
  }
  if (p < 0.001) {
    return `< ${apa ? apaBounded(fmt, 0.001, 3) : fixed(fmt, 0.001, 3)}`;
  }
  return apa ? apaBounded(fmt, p, 3) : fixed(fmt, p, 3);
}

/** Up to `digits` significant decimals, for estimates of any size. */
export function flexible(fmt: Formatter, value: number, digits = 3): string {
  if (!Number.isFinite(value)) {
    return value > 0 ? "∞" : "—";
  }
  return fmt(value, { maximumFractionDigits: digits }).replace(
    HYPHEN_MINUS,
    "−"
  );
}

/** The p part of an APA results line: "< .001" or "= .043". */
export function apaPClause(fmt: Formatter, p: number): string {
  if (!Number.isFinite(p)) {
    return "= —";
  }
  return p < 0.001
    ? `< ${apaBounded(fmt, 0.001, 3)}`
    : `= ${apaBounded(fmt, p, 3)}`;
}
